import React, { useEffect, useState } from 'react'
import {
  Form,
  FormPasswordInput,
  FormCheckbox
} from '@contrast-security-inc/design-system-forms'
import { Text } from '@contrast-security-inc/design-system-typography'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { FreeTrialTermsText } from './free-trial-terms-text'
import { useIntl } from 'react-intl'
import { usePostFreeTrialActivation } from './api/use-post-free-trial-activation'
import { useFormContext } from 'react-hook-form'
import { css } from '@emotion/css'
import {
  IconWarning,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { GridLoading } from '../../legacy-design-system/grid-loading'
import { ProgressBar } from '@contrast-security-inc/design-system-data-bars'
import {
  createFreeTrialRegistrationSchema,
  defaultPasswordPolicy,
  fetchPasswordPolicy,
  PasswordPolicy
} from './utils/free-trial-password-validation'
import { Stack } from '@contrast-security-inc/design-system-layouts'

const signPageUrl = '/Contrast/static/ng/index.html#/pages/signin'

export interface FreeTrialRegistrationFormSchema {
  password: string
  confirmPassword: string
  terms: boolean
}

interface FreeTrialRegistrationFormProps {
  isSubmitting: boolean
  formatMessage: Function
  progress: any
  minLength: number
  minUpper: number
  minLower: number
  minNumbers: number
  minSymbols: number
}

const formGroup = css`
  margin-bottom: 20px;
  margin-left: -15px;
  margin-right: -15px;
`

const spinnerClass = css`
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  color: ${theme.color.neutral70};
`

export const FreeTrialRegistrationForm = ({
  isSubmitting,
  formatMessage,
  progress,
  minLength,
  minUpper,
  minLower,
  minNumbers,
  minSymbols
}: FreeTrialRegistrationFormProps) => {
  const { formState } = useFormContext<FreeTrialRegistrationFormSchema>()

  return (
    <div>
      <div className={formGroup}>
        <FormPasswordInput<FreeTrialRegistrationFormSchema, 'password'>
          name="password"
          testId="password-input"
          label="password"
          help={{
            TooltipProps: {
              body: formatMessage(
                {
                  id: 'pluralCount_defaultPasswordPolicy_validation'
                },
                {
                  minLength,
                  minUpper,
                  minLower,
                  minNumbers,
                  minSymbols
                }
              )
            }
          }}
        />
      </div>
      <div className={formGroup}>
        <FormPasswordInput<FreeTrialRegistrationFormSchema, 'confirmPassword'>
          name="confirmPassword"
          testId="confirm-password-input"
          label="confirm password"
        />
      </div>
      <div className={formGroup}>
        <ProgressBar
          total={{ value: 5 }}
          progress={progress}
          labelType="footer"
        />
      </div>
      <Stack flexDirection="column" spacing="4px" className={formGroup}>
        <Stack flexDirection="row" alignItems="center">
          <FormCheckbox<FreeTrialRegistrationFormSchema, 'terms'>
            name="terms"
            testId="terms-checkbox"
          ></FormCheckbox>
          <FreeTrialTermsText />
        </Stack>
        <Stack alignItems="center" spacing="4px">
          {formState?.errors && formState.errors.terms && (
            <Text font="metadata" color="red70">
              <IconWarning size="sm" color="red50" />
              {formatMessage({ id: 'validation_acceptTermsAndConditions' })}
            </Text>
          )}
        </Stack>
      </Stack>
      <div className={formGroup}>
        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block text-center"
          disabled={isSubmitting || !formState?.isValid}
          data-testid="free-trial-registration-form-submit-button"
        >
          {formatMessage({ id: 'detail_savePassword' })}
        </button>
      </div>
      <GridLoading hide={!isSubmitting} className={spinnerClass} />
    </div>
  )
}

export const FreeTrialRegistration = () => {
  const { formatMessage } = useIntl()
  const [progress, setProgress] = useState({ value: 0, color: 'white' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutate: postFreeTrialActivation } = usePostFreeTrialActivation({
    onSuccess: () => window.location.assign(signPageUrl),
    onError: () => {
      toast(
        formatMessage({
          id: 'detail_freeTrailActivationFailedMsg'
        }),
        {
          variant: 'error'
        }
      )
      setIsSubmitting(false)
    }
  })

  const [data, setData] = useState<PasswordPolicy>(defaultPasswordPolicy)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    void fetchPasswordPolicy(setData, setLoading, formatMessage)
  }, [])

  return (
    <div data-testid="free-trial-registration-form">
      {loading ? (
        <GridLoading className={spinnerClass} />
      ) : (
        <Form<FreeTrialRegistrationFormSchema>
          defaultValues={{
            password: '',
            confirmPassword: '',
            terms: false
          }}
          createSchema={() =>
            createFreeTrialRegistrationSchema(
              formatMessage,
              setProgress,
              data.min_length,
              data.min_upper,
              data.min_lower,
              data.min_numbers,
              data.min_symbols
            )
          }
          onSubmitSuccess={(data) => {
            setIsSubmitting(true)
            postFreeTrialActivation({
              freeTrialToken: window.location.hash.split('/').pop() || '',
              password: data.password
            })
          }}
        >
          <FreeTrialRegistrationForm
            isSubmitting={isSubmitting}
            formatMessage={formatMessage}
            progress={progress}
            minLength={data.min_length}
            minUpper={data.min_upper}
            minLower={data.min_lower}
            minNumbers={data.min_numbers}
            minSymbols={data.min_symbols}
          ></FreeTrialRegistrationForm>
        </Form>
      )}
    </div>
  )
}
